import { Order, SuggestedTour, User } from '../types'
import { ZONES } from '../constants/zones'

/**
 * Generates suggested tours for the next day based on orders ready for delivery
 * Prioritizes returns (RETOUR_DEPOT) and groups by zone
 */
export function generateSuggestedTours(
  orders: Order[],
  drivers: User[],
  targetDate: string,
): SuggestedTour[] {
  // Get orders ready for delivery (STOCKEE_LIVRAISON or RETOUR_DEPOT)
  const readyOrders = orders.filter(
    (o) => o.status === 'STOCKEE_LIVRAISON' || o.status === 'RETOUR_DEPOT',
  )

  if (readyOrders.length === 0) {
    return []
  }

  // Group orders by zone and sort by priority
  const ordersByZone = ZONES.reduce(
    (acc, zone) => {
      const zoneOrders = readyOrders
        .filter((o) => o.zone === zone)
        .sort((a, b) => {
          // Sort by priority (higher first), then by delivery attempts (more attempts = higher priority)
          if (b.priority !== a.priority) {
            return b.priority - a.priority
          }
          return b.deliveryAttempts - a.deliveryAttempts
        })

      if (zoneOrders.length > 0) {
        acc[zone] = zoneOrders
      }
      return acc
    },
    {} as Record<string, Order[]>,
  )

  // Create suggested tours for each zone with orders
  const suggestedTours: SuggestedTour[] = []

  Object.entries(ordersByZone).forEach(([zone, zoneOrders]) => {
    // Count priority orders (returns)
    const priorityCount = zoneOrders.filter(
      (o) => o.status === 'RETOUR_DEPOT',
    ).length

    // For now, create one tour per zone
    // In a more advanced system, you might split large zones into multiple tours
    suggestedTours.push({
      zone,
      date: targetDate,
      orderIds: zoneOrders.map((o) => o.id),
      priorityCount,
      totalOrders: zoneOrders.length,
    })
  })

  return suggestedTours
}

/**
 * Calculates priority for an order
 * Returns get +100 priority, each delivery attempt adds +10
 */
export function calculateOrderPriority(order: Order): number {
  let priority = 0

  // Base priority for returns
  if (order.status === 'RETOUR_DEPOT') {
    priority += 100
  }

  // Add priority based on delivery attempts
  priority += order.deliveryAttempts * 10

  // Add priority based on how overdue the delivery is
  const estimatedDate = new Date(order.estimatedDeliveryDate)
  const today = new Date()
  const daysOverdue = Math.floor(
    (today.getTime() - estimatedDate.getTime()) / (1000 * 60 * 60 * 24),
  )

  if (daysOverdue > 0) {
    priority += daysOverdue * 5
  }

  return Math.max(0, priority)
}

/**
 * Gets the next available delivery date (tomorrow)
 */
export function getNextDeliveryDate(): string {
  const tomorrow = new Date()
  tomorrow.setDate(tomorrow.getDate() + 1)
  return tomorrow.toISOString().split('T')[0]
}
